/* eslint-disable indent */
import { useState, useCallback, useEffect, ChangeEvent } from 'react';
import Modal from '@/components/Modal';
import { Select } from '@/components/Controls/Select';
import { damageLevelOptions } from '@/utils/constant.utils';
import { getDamageValue, getDamageByValue } from '@/utils/ia.utils';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { focusButton } from '@/utils/button.utils';
import { TFunction } from 'i18next';
type EditElementModalProps = {
  t?: TFunction<undefined>;
  id: string;
  equipmentClassId?: number;
  secondButtonRef?: React.RefObject<HTMLButtonElement>;
  onConfirm?: (newValue: string) => void;
  confirmLabel: string;
  defaultValue?: string;
  isElement: boolean;
};

const EditElementModal = ({
  t,
  id,
  equipmentClassId,
  secondButtonRef,
  onConfirm,
  confirmLabel,
  defaultValue,
  isElement,
}: EditElementModalProps) => {
  const [valueTemp, setValueTemp] = useState<string>();
  const [dmgLevel, setDmgLevel] = useState<number>();
  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { uniqueDamagesSelectOptions, allDamagesSelectOptions } =
    useGetModerationDamages();

  useEffect(() => {
    if (valueTemp || dmgLevel) {
      focusButton(secondButtonRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTemp, dmgLevel]);

  useEffect(() => {
    if (isElement) {
      // For elements, initialize valueTemp with defaultValue
      if (defaultValue && defaultValue !== valueTemp) {
        setValueTemp(defaultValue);
      }
    } else {
      // For damages, extract the damage name and level from the label
      const damage = getDamageByValue(defaultValue, allDamagesSelectOptions);
      if (damage && damage?.label !== valueTemp) {
        setValueTemp(damage?.label);
        setDmgLevel(damage?.severity);
      }
    }
  }, [defaultValue, isElement]);

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setValueTemp(e.target.value);
  };

  const handleLevelChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setDmgLevel(Number(e.target.value));
  };

  const onConfirmEdit = useCallback(() => {
    if (onConfirm) {
      onConfirm(
        isElement
          ? valueTemp
          : getDamageValue(valueTemp, dmgLevel, allDamagesSelectOptions),
      );
    }
  }, [isElement, valueTemp, defaultValue, dmgLevel]);

  return (
    <Modal
      id={id}
      title={isElement ? t('modifyEquipment') : t('modifyDamage')}
      size="small"
      centerY
      showSecondBtn
      secondButtonRef={secondButtonRef}
      onClickSecondBtn={onConfirmEdit}
      secondBtnLabel={confirmLabel}
    >
      <div className="grid gap-4">
        <div>
          <div className="text-2sm text-slate-500 font-medium mb-0.5">
            <span>
              {isElement ? t?.('common:equipment') : t?.('common:damage')}
            </span>
            <span className="ml-1 mt-0.5 text-red-FF2929 text-md">*</span>
          </div>
          <Select
            selectClassName="w-[319px] text-2sm"
            options={
              isElement
                ? equipmentClassId
                  ? equipmentsSelectOptions.filter(
                      (option) =>
                        option?.value === equipmentClassId?.toString(),
                    )
                  : equipmentsSelectOptions
                : uniqueDamagesSelectOptions
            }
            onChange={handleSelectChange}
            value={valueTemp || ''}
          />
        </div>
        {!isElement && (
          <div>
            <div className="text-2sm text-slate-500 font-medium mb-0.5">
              {t?.('common:severity')}
            </div>
            <Select
              selectClassName="text-2sm"
              options={damageLevelOptions}
              value={dmgLevel || 1}
              onChange={handleLevelChange}
              defaultValue={1}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditElementModal;
